import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { isCampaignActive } from '../../services/campaignService';
import paymentService from '../../services/paymentService';
import WhatsNextWidget from '../../components/dashboard/WhatsNextWidget';
import StatusTooltip from '../../components/common/StatusTooltip';
import QuickStartGuide from '../../components/common/QuickStartGuide';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ArrowRight, BarChart } from 'lucide-react';

const Dashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const [stats, setStats] = useState({
    activeCampaigns: 0,
    completedCampaigns: 0,
    totalEarnings: 0,
    averageRating: 0
  });
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [earningsData, setEarningsData] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(100);
  const [showProfileBanner, setShowProfileBanner] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentUser) return;

      try {
        // Fetch campaigns where the user is a participant
        const campaignsQuery = query(
          collection(db, 'campaignParticipants'),
          where('influencerId', '==', currentUser.uid)
        );
        
        const campaignsSnapshot = await getDocs(campaignsQuery);
        
        // Initialize counters for stats
        let completedCount = 0;
        let ratingSum = 0;
        let ratingCount = 0;
        let activeCampaignsCount = 0;
        
        // Fetch completed payments for the user
        const completedPayments = await paymentService.getPaymentsByInfluencerId(currentUser.uid, {
          status: 'completed'
        });
        

        
        // Get unique campaign IDs from completed payments
        const completedCampaignIds = new Set(completedPayments.map(payment => payment.campaignId));


        
        // Count completed reviews directly from completed payments
        const completedReviewsCount = completedPayments.length;
        
        // Calculate total earnings from completed payments
        const totalEarnings = await paymentService.calculateTotalEarningsByInfluencer(currentUser.uid, {
          completedOnly: true
        });

        // Derive monthly earnings data for the performance graph
        const monthlyMap = {};
        completedPayments.forEach((payment) => {
          let date;
          if (payment.createdAt?.seconds) {
            date = new Date(payment.createdAt.seconds * 1000);
          } else if (payment.createdAt instanceof Date) {
            date = payment.createdAt;
          } else if (typeof payment.createdAt === 'string') {
            date = new Date(payment.createdAt);
          } else if (payment.paidAt?.seconds) {
            date = new Date(payment.paidAt.seconds * 1000);
          } else {
            return;
          }
          const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          monthlyMap[key] = (monthlyMap[key] || 0) + (Number(payment.amount) || 0);
        });

        // Build last 8 months array (including months with zero earnings)
        const now = new Date();
        const months = [];
        for (let i = 7; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          const label = d.toLocaleDateString('en', { month: 'short' });
          months.push({ month: label, earnings: Math.round(monthlyMap[key] || 0) });
        }
        setEarningsData(months);
        


        
        // Process campaign participants
        const participantPromises = [];
        

        
        campaignsSnapshot.forEach((participantDoc) => {
          const participantData = participantDoc.data();
          
          // Count completed campaigns based on status
          if (participantData.status === 'completed') {
            completedCount++;
            if (participantData.rating) {
              ratingSum += participantData.rating;
              ratingCount++;
            }
          }
          
          // Fetch the associated campaign details
          const promise = fetchCampaignDetails(participantData.campaignId).then(async (campaignDetails) => {
            if (!campaignDetails) {

              return null;
            }
            
            // Check if campaign is truly active (not ended)
            const isCampaignStillActive = isCampaignActive(campaignDetails);

            // Fetch actual proof submission status AND draft status
            let submissionStatus = null;
            let contentReviewStatus = null;
            let draftStatus = participantData.draftStatus || 'not_submitted';
            let draftFeedback = participantData.draftFeedback || '';
            

            
            try {
              // Check for proof submissions
              const proofsQuery = query(
                collection(db, 'proofs'),
                where('campaignId', '==', participantData.campaignId),
                where('influencerId', '==', currentUser.uid),
                orderBy('submittedAt', 'desc'),
                limit(1)
              );
              const proofsSnapshot = await getDocs(proofsQuery);
              

              
              if (!proofsSnapshot.empty) {
                const latestProof = proofsSnapshot.docs[0].data();
                submissionStatus = 'submitted';
                
                // Use the proof's own status as the content review status
                contentReviewStatus = latestProof.status; // 'approved', 'revision_requested', etc.
                

              }
              

            } catch (error) {
              console.error('Error fetching submission/review status:', error);
            }
            
            // Debug campaign details

            
            // Get campaign title - handle different field names
            let campaignTitle = 'Untitled Campaign';
            if (campaignDetails.title) {
              campaignTitle = campaignDetails.title;
            } else if (campaignDetails.campaignName) {
              campaignTitle = campaignDetails.campaignName;
            } else if (campaignDetails.name) {
              campaignTitle = campaignDetails.name;
            } else if (campaignDetails.productName) {
              campaignTitle = campaignDetails.productName;
            }
            
            // Format compensation if it exists - handle different field names
            let formattedCompensation = 'Not specified';
            // Check all possible compensation field names
            const compensationValue = campaignDetails.compensation || 
                                     campaignDetails.compensationAmount || 
                                     campaignDetails.budget || 
                                     null;
            
            if (compensationValue) {
              if (typeof compensationValue === 'object') {
                const comp = [];
                if (compensationValue.cash) {
                  comp.push(`₱${compensationValue.cash.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`);
                }
                if (compensationValue.products) {
                  comp.push(compensationValue.products);
                }
                formattedCompensation = comp.join(' + ') || 'Not specified';
              } else if (typeof compensationValue === 'string') {
                // If it's a numeric string, add the peso sign
                if (!isNaN(compensationValue)) {
                  formattedCompensation = `₱${compensationValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
                } else {
                  formattedCompensation = compensationValue;
                }
              } else if (typeof compensationValue === 'number') {
                formattedCompensation = `₱${compensationValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
              }
            }
            
            // Format deadline properly - handle different field names
            let formattedDeadline = 'No deadline';
            // Check all possible deadline field names
            const deadlineValue = campaignDetails.deadline || 
                                 campaignDetails.endDate || 
                                 campaignDetails.reviewDeadline || 
                                 campaignDetails.submissionDeadline || 
                                 null;
            
            if (deadlineValue) {
              if (deadlineValue.seconds) {
                formattedDeadline = new Date(deadlineValue.seconds * 1000).toLocaleDateString();
              } else if (deadlineValue instanceof Date) {
                formattedDeadline = deadlineValue.toLocaleDateString();
              } else if (typeof deadlineValue === 'string') {
                try {
                  formattedDeadline = new Date(deadlineValue).toLocaleDateString();
                } catch (e) {
                  console.error('Error formatting date:', e);
                }
              }
            }
            
            return {
              id: participantData.campaignId,
              participantId: participantDoc.id,
              campaignName: campaignTitle,
              brandName: campaignDetails.brandName,
              compensation: formattedCompensation,
              status: participantData.status,
              deadline: formattedDeadline,
              submissionStatus: submissionStatus,
              contentReviewStatus: contentReviewStatus,
              draftStatus: draftStatus,
              draftFeedback: draftFeedback,
              requiresDraftApproval: campaignDetails.requireDraftApproval !== false, // Default to true for new workflow
              ...participantData,
              campaignDetails: campaignDetails,
              isCampaignActive: isCampaignStillActive
            };
          });
          
          participantPromises.push(promise);
        });
        
        // Wait for all campaign details to be fetched
        const resolvedCampaigns = await Promise.all(participantPromises);
        const validCampaigns = resolvedCampaigns.filter(campaign => campaign !== null);
        

        
        // Count active campaigns (approved participants in active campaigns)
        const trulyActiveCampaigns = validCampaigns.filter(campaign => {
          return (campaign.status === 'approved' || campaign.status === 'active') && campaign.isCampaignActive;
        });
        activeCampaignsCount = trulyActiveCampaigns.length;

        
        setStats({
          activeCampaigns: activeCampaignsCount,
          completedCampaigns: completedCount,
          totalEarnings: totalEarnings || 0,
          averageRating: ratingCount > 0 ? ratingSum / ratingCount : 0
        });
        
        // Sort campaigns to show active ones first
        const sortedCampaigns = [...validCampaigns].sort((a, b) => {
          const aIsActive = a.status === 'active' && a.isCampaignActive;
          const bIsActive = b.status === 'active' && b.isCampaignActive;
          
          if (aIsActive && !bIsActive) return -1;
          if (!aIsActive && bIsActive) return 1;
          
          return b.joinedAt?.seconds - a.joinedAt?.seconds || 0;
        });
        
        // Limit to 5 campaigns for display
        setRecentCampaigns(sortedCampaigns.slice(0, 5));
        
        // Show QuickStartGuide for new users who haven't seen it
        const hasSeenGuide = localStorage.getItem('quickStartGuide_influencer_seen');
        if (!hasSeenGuide && sortedCampaigns.length <= 1) {
          // Show guide if user has 0 or 1 campaign (just applied)
          setShowQuickStart(true);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchDashboardData();
    }
  }, [currentUser]);

  // Fetch influencer profile and calculate completion percentage
  useEffect(() => {
    const fetchProfileCompletion = async () => {
      if (!currentUser?.uid) return;
      try {
        const profileDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!profileDoc.exists()) return;
        const data = profileDoc.data();

        const checks = [
          !!(data.displayName || data.name),
          !!(data.bio || data.description),
          !!(data.profilePicture || data.photoURL),
          !!(Array.isArray(data.categories) && data.categories.length > 0),
          !!(data.location || data.city),
          !!(data.socialMedia && Object.values(data.socialMedia).some((v) => v && String(v).trim() !== '')),
          !!(data.followers || data.followerCount),
          !!(data.niche || data.specialty),
          !!(Array.isArray(data.contentTypes) && data.contentTypes.length > 0),
          !!(data.rates || data.pricing),
        ];

        // Weights: socialMedia is 15%, rates/pricing is 5%, rest are 10% each
        const weights = [10, 10, 10, 10, 10, 15, 10, 10, 10, 5];
        let earned = 0;
        checks.forEach((passed, i) => {
          if (passed) earned += weights[i];
        });
        const percentage = Math.min(100, Math.round(earned));
        setProfileCompletion(percentage);

        const dismissed = localStorage.getItem('profileCompleteDismissed_influencer');
        setShowProfileBanner(!dismissed && percentage < 100);
      } catch (err) {
        // Silently fail — banner is non-critical
      }
    };

    fetchProfileCompletion();
  }, [currentUser?.uid]);

  // Import campaign service for fetching campaign details
  const fetchCampaignDetails = async (campaignId) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignSnap = await getDoc(campaignRef);
      
      if (campaignSnap.exists()) {
        const data = campaignSnap.data();

        
        // Get brand details
        let brandName = 'Unknown Brand';
        if (data.brandId) {
          try {
            const brandRef = doc(db, 'users', data.brandId);
            const brandSnap = await getDoc(brandRef);
            if (brandSnap.exists()) {
              const brandData = brandSnap.data();
              // Use businessName for brand accounts
              brandName = brandData.businessName || 'Test Brand';

            }
          } catch (brandError) {
            console.error('Error fetching brand details:', brandError);
          }
        } else if (data.brand && typeof data.brand === 'object' && data.brand.name) {
          // Some campaigns might have brand info embedded
          brandName = data.brand.name;
        } else if (data.brandName && data.brandName !== 'Francis') {
          // Use brandName if it exists and isn't the user's name
          brandName = data.brandName;
        }
        
        // Add brand name to campaign data
        return { ...data, brandName };
      }
      return null;
    } catch (error) {
      console.error('Error fetching campaign details:', error);
      return null;
    }
  };

  // Decorative sparkline bar heights per stat card (visual only, not claimed data)
  const sparkline = {
    activeCampaigns: [40, 55, 45, 65, 50, 70, 60],
    completedCampaigns: [30, 45, 40, 55, 60, 50, 65],
    averageRating: [50, 48, 55, 52, 58, 54, 56],
  };
  // Real monthly earnings magnitudes for the Total Earnings sparkline
  const earningsSparkline = earningsData.length > 0
    ? (() => {
        const max = Math.max(...earningsData.map(d => d.earnings), 1);
        return earningsData.slice(-7).map(d => Math.max(10, Math.round((d.earnings / max) * 70)));
      })()
    : [20, 20, 20, 20, 20, 20, 20];

  const statsCards = [
    { name: 'Active Campaigns', value: stats.activeCampaigns || 0, unit: 'Campaigns', bars: sparkline.activeCampaigns },
    { name: 'Completed Reviews', value: stats.completedCampaigns || 0, unit: 'Reviews', bars: sparkline.completedCampaigns },
    { name: 'Total Earnings', value: `₱${(stats.totalEarnings || 0).toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, unit: '', bars: earningsSparkline },
    { name: 'Average Rating', value: stats.averageRating ? stats.averageRating.toFixed(1) : 'N/A', unit: stats.averageRating ? '/ 5.0' : '', bars: sparkline.averageRating },
  ];

  // Build recommendations based on current stats and campaigns
  const recommendations = [];
  if (stats.activeCampaigns === 0) {
    recommendations.push({ text: 'Browse available campaigns to start earning', link: '/influencer/campaigns', accent: 'border-l-blue-500' });
  }
  if (stats.activeCampaigns > 0) {
    const hasUnsubmitted = recentCampaigns.some(c => c.status === 'approved' && c.draftStatus === 'not_submitted');
    if (hasUnsubmitted) {
      recommendations.push({ text: 'Submit your content draft for active campaigns', link: '/influencer/campaigns', accent: 'border-l-orange-500' });
    }
  }
  if (stats.averageRating > 0 && stats.averageRating < 4.0 && stats.completedCampaigns > 0) {
    recommendations.push({ text: 'Focus on detailed, authentic reviews to improve your rating', link: '/influencer/campaigns', accent: 'border-l-yellow-500' });
  }
  if (stats.totalEarnings > 0) {
    recommendations.push({ text: "You're earning! Check your payment history", link: '/influencer/payments', accent: 'border-l-green-500' });
  }
  recommendations.push({ text: 'Complete your profile to attract more brands', link: '/influencer/profile', accent: 'border-l-primary' });
  // Limit to 4
  const topRecommendations = recommendations.slice(0, 4);

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Welcome back, {userProfile?.displayName || currentUser?.displayName || 'Influencer'}</h2>
        <p className="text-gray-600">Here's an overview of your activity and campaigns</p>
      </div>

      {/* Profile Completion Banner */}
      {showProfileBanner && profileCompletion < 100 && (
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-blue-900">
                  Your profile is {profileCompletion}% complete
                </p>
                <Link
                  to="/influencer/profile"
                  className="text-sm font-medium text-blue-700 hover:text-blue-800 underline"
                >
                  Complete your profile
                </Link>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${profileCompletion}%` }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowProfileBanner(false);
                localStorage.setItem('profileCompleteDismissed_influencer', 'true');
              }}
              className="text-blue-400 hover:text-blue-600 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards — minimal, label + value + sparkline */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statsCards.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-sm transition-shadow duration-200"
          >
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{stat.name}</p>
            <div className="mt-3 flex items-end justify-between">
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
                {stat.unit && <span className="text-xs text-gray-400">{stat.unit}</span>}
              </div>
              {/* Decorative sparkline */}
              <div className="flex items-end gap-0.5 h-8">
                {stat.bars.map((h, i) => (
                  <div
                    key={i}
                    className="w-1 bg-gray-200 rounded-full"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Performance Graph + Recommendations — two-column section */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
        {/* Performance Graph */}
        <div className="lg:col-span-3 bg-white rounded-lg shadow p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-800">Performance Overview</h3>
            <p className="text-sm text-gray-500">Your earnings over time</p>
          </div>
          {earningsData.some(d => d.earnings > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={earningsData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="earningsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B5BA5" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3B5BA5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={(v) => `₱${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                <Tooltip
                  formatter={(value) => [`₱${value.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, 'Earnings']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                />
                <Area
                  type="monotone"
                  dataKey="earnings"
                  stroke="#3B5BA5"
                  strokeWidth={2}
                  fill="url(#earningsGradient)"
                  dot={{ fill: '#3B5BA5', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[280px] text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <BarChart className="h-8 w-8 text-gray-300" />
              </div>
              <p className="text-gray-500 text-sm">No earnings data yet</p>
              <p className="text-gray-400 text-xs mt-1">Complete campaigns to see your performance here</p>
            </div>
          )}
        </div>

        {/* Recommendations */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-800">Recommendations</h3>
            <p className="text-sm text-gray-500">Suggested actions to grow</p>
          </div>
          <div className="space-y-3">
            {topRecommendations.map((rec, i) => (
              <Link
                key={i}
                to={rec.link}
                className={`block border-l-4 ${rec.accent} pl-4 py-2 hover:bg-gray-50 rounded-r-md transition-colors duration-200 group`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">{rec.text}</p>
                  <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Start Guide - Show for new users */}
      {showQuickStart && (
        <QuickStartGuide 
          userType="influencer" 
          onClose={() => {
            setShowQuickStart(false);
            localStorage.setItem('quickStartGuide_influencer_seen', 'true');
          }}
        />
      )}

      {/* What's Next Widget */}
      <div className="mb-8">
        <WhatsNextWidget 
          userType="influencer"
          actionItems={recentCampaigns.slice(0, 3).map((campaign, index) => {

            
            // Determine priority based on deadline and status
            let priority = 'normal';
            let category = 'waiting';
            let icon = '📬';
            let title = campaign.campaignName;
            let description = `${campaign.brandName} - ${campaign.status}`;
            let action = 'View Campaign';
            let dueDate = campaign.deadline || 'No deadline';
            
            if (campaign.status === 'active' && campaign.isCampaignActive) {
              priority = 'high';
              category = 'urgent';
              icon = '🚨';
              title = 'Active Campaign';
              description = `${campaign.campaignName} - Create and submit content`;
              action = 'Create Content';
            } else if (campaign.status === 'pending') {
              priority = 'normal';
              category = 'waiting';
              icon = '⏳';
              title = 'Application Pending';
              description = `${campaign.campaignName} - Waiting for brand response`;
              action = 'View Status';
            } else if (campaign.status === 'approved') {
              // Check workflow status - prioritize draft workflow if enabled
              if (campaign.requiresDraftApproval) {
                // Pre-approval workflow - check draft status first
                if (campaign.draftStatus === 'rejected' || campaign.draftStatus === 'revision_requested') {
                  // Draft needs revision
                  priority = 'high';
                  category = 'urgent';
                  icon = '🔄';
                  title = 'Draft Revision Required';
                  description = `${campaign.campaignName} - Update and resubmit your content draft`;
                  action = 'Fix Draft';
                } else if (campaign.draftStatus === 'pending' || campaign.draftStatus === 'submitted') {
                  // Draft submitted, waiting for brand approval
                  priority = 'normal';
                  category = 'waiting';
                  icon = '⏳';
                  title = 'Draft Under Review';
                  description = `${campaign.campaignName} - Draft submitted, awaiting brand approval`;
                  action = 'View Status';
                } else if (campaign.draftStatus === 'approved') {
                  // Draft approved, check proof status
                  if (campaign.contentReviewStatus === 'approved') {
                    // Content is approved - waiting for campaign end for payment
                    priority = 'normal';
                    category = 'waiting';
                    icon = '✅';
                    title = 'Content Approved';
                    description = `${campaign.campaignName} - Waiting for campaign end to receive payment`;
                    action = 'View Campaign';
                  } else if (campaign.contentReviewStatus === 'revision_requested') {
                    // Proof needs revision
                    priority = 'high';
                    category = 'urgent';
                    icon = '🔄';
                    title = 'Proof Revision Required';
                    description = `${campaign.campaignName} - Update and resubmit your proof`;
                    action = 'Fix Proof';
                  } else if (campaign.submissionStatus === 'submitted') {
                    // Proof submitted, waiting for brand review
                    priority = 'normal';
                    category = 'waiting';
                    icon = '⏳';
                    title = 'Proof Under Review';
                    description = `${campaign.campaignName} - Proof submitted, awaiting brand review`;
                    action = 'View Status';
                  } else {
                    // Draft approved, ready to post and submit proof
                    priority = 'medium';
                    category = 'action_required';
                    icon = '📝';
                    title = 'Ready to Post Content';
                    description = `${campaign.campaignName} - Draft approved, post content and submit proof`;
                    action = 'Submit Proof';
                  }
                } else {
                  // No draft submitted yet (not_submitted)
                  priority = 'medium';
                  category = 'action_required';
                  icon = '📝';
                  title = 'Submit Draft';
                  description = `${campaign.campaignName} - Submit your content draft for brand approval`;
                  action = 'Submit Draft';
                }
              } else {
                // Legacy workflow (no draft approval required)
                if (campaign.contentReviewStatus === 'approved') {
                  // Content is approved - waiting for campaign end for payment
                  priority = 'normal';
                  category = 'waiting';
                  icon = '✅';
                  title = 'Content Approved';
                  description = `${campaign.campaignName} - Waiting for campaign end to receive payment`;
                  action = 'View Campaign';
                } else if (campaign.contentReviewStatus === 'revision_requested') {
                  // Content needs revision
                  priority = 'high';
                  category = 'urgent';
                  icon = '🔄';
                  title = 'Revision Required';
                  description = `${campaign.campaignName} - Update and resubmit content`;
                  action = 'Fix Content';
                } else if (campaign.submissionStatus === 'submitted') {
                  // Content submitted, waiting for brand review
                  priority = 'normal';
                  category = 'waiting';
                  icon = '⏳';
                  title = 'Under Review';
                  description = `${campaign.campaignName} - Content submitted, awaiting brand review`;
                  action = 'View Status';
                } else {
                  // Application approved but no content submitted yet
                  priority = 'medium';
                  category = 'action_required';
                  icon = '🎨';
                  title = 'Ready to Create';
                  description = `${campaign.campaignName} - Start creating content`;
                  action = 'Start Creating';
                }
              }
            }
            
            return {
              id: campaign.id || index,
              priority,
              icon,
              title,
              description,
              action,
              link: `/influencer/campaigns/${campaign.id}`,
              dueDate,
              category
            };
          })}
        />
      </div>

      {/* Recent Campaigns */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">Recent Campaigns</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Compensation</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentCampaigns.length > 0 ? (
                recentCampaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{campaign.campaignName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{campaign.brandName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{campaign.compensation}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusTooltip status={campaign.status} type="campaign">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${campaign.status === 'active' ? 'bg-green-100 text-green-800' : 
                            campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' : 
                            'bg-yellow-100 text-yellow-800'}`}>
                          {campaign.status}
                        </span>
                      </StatusTooltip>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{campaign.deadline}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link to={`/influencer/campaigns/${campaign.id}`} className="text-primary hover:text-primary-dark">View</Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                    No campaigns found. <Link to="/influencer/campaigns" className="text-primary hover:text-primary-dark">Browse available campaigns</Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-gray-200">
          <Link to="/influencer/campaigns" className="text-primary hover:text-primary-dark text-sm font-medium">View all campaigns</Link>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">Tips for Successful Reviews</h3>
        </div>
        <div className="px-6 py-4">
          <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600">
            <li>Be honest and authentic in your product reviews</li>
            <li>Provide detailed feedback with both pros and cons</li>
            <li>Include high-quality photos and videos when possible</li>
            <li>Submit your reviews before the deadline</li>
            <li>Maintain professional communication with brands</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
